/**
 * Shape files on the map (MB's R152, decision 921 on Q819 B; docs/specs/map.md §34). A shape file dropped on the
 * Map (or chosen with "Add map files") is read by the core (`shape_read`, `teslyn_core::map::shapefile`: the shapes
 * in the file's own coordinates, the `.dbf` values and the `.prj` text). This module holds the rules:
 *
 * - **Where the shapes are.** The `.prj` says it, and it is never guessed: WGS 84, GDA94 or GDA2020 in degrees, or a
 *   UTM or MGA zone on one of them, in metres. Anything else, or no `.prj`, is refused in words that say what to do.
 *   The shapes are reprojected to the map's WGS 84 with the map's own inverse (`utmToLatLng`, map-geo.ts); the GDA
 *   to WGS 84 datum shift (under 2 m) is not applied, as for every other layer. AGD66 and AGD84 (about 200 m from
 *   WGS 84) are refused, since drawing them without the shift would put the shapes in the wrong place.
 * - **What it is.** Teslyn asks (decision 921): hazards, property boundaries or another layer. Hazards and other
 *   layers are saved as one shared record of the project (`shape_layer`), drawn on every laptop of the project,
 *   Teslyn Cloud's staff maps and Teslyn Mobile; hazards are also offered for the pilots' hazard pack, and so reach a
 *   client only when staff publish the pack to the client portal (decision 819). Property boundaries become
 *   landholder boundaries (`land_parcel`, decision 618), each area one boundary, in a folder named after the file,
 *   with the `.dbf` values as their attributes (an OWNER or CONTACT field as contacts, staff only, decision 83).
 */
import type { KmlDocument, KmlFolder, KmlGeometry, KmlPlacemark, LandParcel } from './map-kml.js';
import { landParcelsFromKml } from './map-kml.js';
import { boundsOf, utmToLatLng, type GeoBounds } from './map-geo.js';
import type { LatLng } from './planning.js';
import type { FeatureStyle, LayerResult, MapFeature } from './map-layers.js';
import { kmlGeometryToMap } from './map-layers.js';

// ------------------------------------------------------------------------------------------- what the core reads

export type ShapeXy = readonly [number, number];

export type ShapeGeometryRead =
  | { readonly type: 'point'; readonly at: ShapeXy }
  | { readonly type: 'multi_point'; readonly points: readonly ShapeXy[] }
  | { readonly type: 'polyline'; readonly parts: ReadonlyArray<readonly ShapeXy[]> }
  | {
      readonly type: 'polygon';
      readonly polygons: ReadonlyArray<{
        readonly outer: readonly ShapeXy[];
        readonly holes: ReadonlyArray<readonly ShapeXy[]>;
      }>;
    };

/** One shape file as `shape_read` returns it (`teslyn_core::map::shapefile::ShapeRead`). */
export interface ShapeRead {
  /** The base name, `F26048_Milgun_fnl_BND`. */
  readonly name: string;
  /** The `.shp` path, or `<zip>!<entry>`. */
  readonly source: string;
  /** The file names read, `.shp` first. */
  readonly files: readonly string[];
  /** The paths on this laptop of the files read (the `.zip` for one inside a zip). */
  readonly paths: readonly string[];
  readonly prj: string | null;
  readonly shape_type: string;
  readonly fields: readonly string[];
  readonly dbf_rows: number | null;
  readonly bbox: readonly [number, number, number, number];
  readonly records: ReadonlyArray<{
    readonly geometry: ShapeGeometryRead | null;
    readonly attributes: ReadonlyArray<readonly [string, string]>;
  }>;
  /** The `.dbf`'s code page as read (from its `.cpg`, else its header); null or absent when neither names one. */
  readonly encoding?: string | null;
  /** What the core left out or could not pair, in words. */
  readonly notes: readonly string[];
}

// ------------------------------------------------------------------------------------------------ the .prj (WKT)

interface WktNode {
  readonly key: string;
  readonly args: ReadonlyArray<string | number | WktNode>;
}

/** A WKT text (version 1 as ESRI and GDAL write `.prj` files, or version 2) as a tree, or null. */
export function parseWkt(text: string): WktNode | null {
  let i = 0;
  const s = text.trim();
  const space = (): void => {
    while (i < s.length && /\s/.test(s[i]!)) i += 1;
  };
  const node = (): WktNode | null => {
    space();
    const m = /^[A-Za-z_][A-Za-z0-9_]*/.exec(s.slice(i));
    if (!m) return null;
    const key = m[0].toUpperCase();
    i += m[0].length;
    space();
    const open = s[i];
    if (open !== '[' && open !== '(') return { key, args: [] };
    const close = open === '[' ? ']' : ')';
    i += 1;
    const args: Array<string | number | WktNode> = [];
    for (;;) {
      space();
      if (i >= s.length) return null;
      if (s[i] === close) {
        i += 1;
        return { key, args };
      }
      if (s[i] === ',') {
        i += 1;
        continue;
      }
      if (s[i] === '"') {
        let out = '';
        i += 1;
        for (;;) {
          if (i >= s.length) return null;
          if (s[i] === '"') {
            if (s[i + 1] === '"') {
              out += '"';
              i += 2;
              continue;
            }
            i += 1;
            break;
          }
          out += s[i];
          i += 1;
        }
        args.push(out);
        continue;
      }
      const num = /^[-+]?(\d+\.?\d*|\.\d+)([eE][-+]?\d+)?/.exec(s.slice(i));
      if (num) {
        args.push(Number(num[0]));
        i += num[0].length;
        continue;
      }
      const sub = node();
      if (!sub) return null;
      args.push(sub);
    }
  };
  const root = node();
  space();
  return root && i >= s.length ? root : null;
}

const children = (n: WktNode): WktNode[] => n.args.filter((a): a is WktNode => typeof a === 'object');
const nameOf = (n: WktNode | undefined): string => (n && typeof n.args[0] === 'string' ? n.args[0] : '');
const numberOf = (n: WktNode | undefined, at = 1): number | null => {
  const v = n?.args[at];
  return typeof v === 'number' ? v : null;
};
/** Every node under `n` (itself included) with one of the keys, depth first. */
function find(n: WktNode, keys: readonly string[]): WktNode[] {
  const out: WktNode[] = [];
  const walk = (x: WktNode): void => {
    if (keys.includes(x.key)) out.push(x);
    children(x).forEach(walk);
  };
  walk(n);
  return out;
}
const squash = (s: string): string => s.toLowerCase().replace(/[^a-z0-9]/g, '');

export type ShapeDatum = 'WGS 84' | 'GDA94' | 'GDA2020';

/** The coordinate system of a shape file, as Teslyn accepts it. */
export type ShapeCrs =
  | { readonly kind: 'geographic'; readonly datum: ShapeDatum; readonly words: string }
  | {
      readonly kind: 'utm';
      readonly datum: ShapeDatum;
      readonly zone: number;
      readonly hemisphere: 'N' | 'S';
      readonly words: string;
    };

export type ShapeCrsResult =
  { readonly ok: true; readonly crs: ShapeCrs } | { readonly ok: false; readonly reason: string };

/** What Teslyn reads, for the refusals. */
export const SHAPE_CRS_ACCEPTED =
  'Teslyn reads shape files in WGS 84, GDA94 or GDA2020, in degrees or in a UTM or MGA zone. Save it in one of them (in QGIS: Export, Save Features As, with one of those coordinate systems) and drop it again.';

function datumOf(name: string): ShapeDatum | 'agd' | null {
  const d = squash(name);
  if (/gda2020|geocentricdatumofaustralia2020/.test(d)) return 'GDA2020';
  if (/gda1994|gda94|geocentricdatumofaustralia1994/.test(d)) return 'GDA94';
  if (/wgs1984|wgs84|worldgeodeticsystem1984/.test(d)) return 'WGS 84';
  if (/agd|australiangeodetic|australian19(66|84)/.test(d)) return 'agd';
  return null;
}

const near = (a: number | null, b: number, tol = 1e-6): boolean => a !== null && Math.abs(a - b) <= tol;

/** The value of a projection parameter, by any of its WKT 1 or WKT 2 names. */
function param(params: readonly WktNode[], ...names: string[]): number | null {
  const want = names.map(squash);
  const p = params.find((x) => want.includes(squash(nameOf(x))));
  return numberOf(p);
}

/**
 * The coordinate system a `.prj` describes, or why Teslyn will not place the shapes (never a guess). `file` names
 * the shape file in the words.
 */
export function shapeCrs(prj: string | null | undefined, file: string): ShapeCrsResult {
  if (!prj || !prj.trim())
    return {
      ok: false,
      reason: `${file} has no .prj file, so Teslyn cannot tell where its shapes are. Drop the .prj of the same name with it (it came with the shape file).`,
    };
  const root = parseWkt(prj);
  if (!root)
    return {
      ok: false,
      reason: `The .prj of ${file} could not be read as a coordinate system. ${SHAPE_CRS_ACCEPTED}`,
    };
  const named = nameOf(root) || root.key;
  const projected = ['PROJCS', 'PROJCRS', 'PROJECTEDCRS'].includes(root.key);
  const geographic = ['GEOGCS', 'GEOGCRS', 'GEOGRAPHICCRS', 'GEODCRS', 'GEODETICCRS'].includes(root.key);
  if (!projected && !geographic)
    return {
      ok: false,
      reason: `The .prj of ${file} describes "${named}", which is not a map coordinate system Teslyn reads. ${SHAPE_CRS_ACCEPTED}`,
    };
  const datumNode = find(root, ['DATUM', 'GEODETICDATUM', 'TRF', 'ENSEMBLE', 'DATUMENSEMBLE'])[0];
  const datum = datumOf(nameOf(datumNode) || named);
  if (datum === 'agd')
    return {
      ok: false,
      reason: `${file} is in ${named}, on the Australian Geodetic Datum (AGD66 or AGD84), about 200 m from where the map draws it. Teslyn does not shift between datums, so it is not drawn. Save it in GDA2020, GDA94 or WGS 84 and drop it again.`,
    };
  if (!datum)
    return {
      ok: false,
      reason: `${file} is in "${named}" (datum "${nameOf(datumNode) || 'not named'}"), which Teslyn cannot place on the map. ${SHAPE_CRS_ACCEPTED}`,
    };
  if (geographic) {
    const unit =
      children(root).find((c) => c.key === 'UNIT' || c.key === 'ANGLEUNIT') ?? find(root, ['ANGLEUNIT'])[0];
    const factor = numberOf(unit);
    if (unit && factor !== null && !near(factor, Math.PI / 180, 1e-9))
      return {
        ok: false,
        reason: `${file} is in ${named} with its angles in ${nameOf(unit) || 'another unit'}, not degrees. ${SHAPE_CRS_ACCEPTED}`,
      };
    const pm = find(root, ['PRIMEM', 'PRIMEMERIDIAN'])[0];
    if (pm && numberOf(pm) !== null && !near(numberOf(pm), 0))
      return {
        ok: false,
        reason: `${file} counts longitude from ${nameOf(pm)}, not Greenwich. ${SHAPE_CRS_ACCEPTED}`,
      };
    return { ok: true, crs: { kind: 'geographic', datum, words: `${datum} (degrees)` } };
  }
  const method =
    find(root, ['PROJECTION', 'METHOD'])
      .map(nameOf)
      .find((n) => n !== '') ?? '';
  if (squash(method) !== 'transversemercator')
    return {
      ok: false,
      reason: `${file} is in ${named}, which uses the ${method.replace(/_/g, ' ') || 'unnamed'} projection. ${SHAPE_CRS_ACCEPTED}`,
    };
  const params = find(root, ['PARAMETER']);
  const fe = param(params, 'false_easting', 'False easting');
  const fn = param(params, 'false_northing', 'False northing');
  const k = param(params, 'scale_factor', 'Scale factor at natural origin');
  const cm = param(params, 'central_meridian', 'Longitude of natural origin', 'longitude_of_center');
  const lat0 = param(params, 'latitude_of_origin', 'Latitude of natural origin', 'latitude_of_center');
  const zone = cm === null ? NaN : (cm + 183) / 6;
  const utm =
    near(fe, 500000, 1e-3) &&
    (near(fn, 0, 1e-3) || near(fn, 10000000, 1e-3)) &&
    near(k, 0.9996, 1e-9) &&
    (lat0 === null || near(lat0, 0)) &&
    Number.isInteger(Math.round(zone)) &&
    near(zone, Math.round(zone), 1e-9) &&
    Math.round(zone) >= 1 &&
    Math.round(zone) <= 60;
  if (!utm)
    return {
      ok: false,
      reason: `${file} is in ${named}, a Transverse Mercator that is not a UTM or MGA zone (central meridian ${cm ?? 'not given'}, scale ${k ?? 'not given'}, false easting ${fe ?? 'not given'}). ${SHAPE_CRS_ACCEPTED}`,
    };
  // the linear unit: WKT 1's UNIT of the projected system, WKT 2's LENGTHUNIT
  const units = [...children(root).filter((c) => c.key === 'UNIT'), ...find(root, ['LENGTHUNIT'])];
  if (units.length === 0 || units.some((u) => !near(numberOf(u), 1, 1e-9)))
    return {
      ok: false,
      reason: `${file} is in ${named}, measured in ${units.map(nameOf).find((n) => n && !/^met(er|re)$/i.test(n)) ?? 'a unit the .prj does not name'}, not metres. ${SHAPE_CRS_ACCEPTED}`,
    };
  const z = Math.round(zone);
  const hemisphere: 'N' | 'S' = near(fn, 10000000, 1e-3) ? 'S' : 'N';
  const words =
    datum !== 'WGS 84' && hemisphere === 'S'
      ? `${datum} / MGA zone ${z}`
      : `${datum} / UTM zone ${z}${hemisphere}`;
  return { ok: true, crs: { kind: 'utm', datum, zone: z, hemisphere, words } };
}

// ---------------------------------------------------------------------------------------- reprojected and named

/** One shape on the map: its name, its shape in WGS 84 and its `.dbf` values. */
export interface ShapeFeature {
  readonly name: string;
  readonly geometry: KmlGeometry;
  readonly attributes: ReadonlyArray<readonly [string, string]>;
}

/** A shape file ready to be added: everything the dialog shows and the save needs. */
export interface ShapeImport {
  readonly name: string;
  /** `F26048_Milgun_fnl_BND.shp` (or `….zip › name.shp`), kept as the layer's source. */
  readonly source: string;
  readonly files: readonly string[];
  readonly paths: readonly string[];
  readonly crs: ShapeCrs;
  readonly features: readonly ShapeFeature[];
  readonly counts: {
    readonly points: number;
    readonly lines: number;
    /** Polygons, and lines that close on themselves (property boundaries take both). */
    readonly areas: number;
  };
  readonly fields: readonly string[];
  readonly bounds: GeoBounds | null;
  readonly notes: readonly string[];
}

export type ShapeImportResult =
  { readonly ok: true; readonly layer: ShapeImport } | { readonly ok: false; readonly reason: string };

/** At most this many shapes in one layer (a state's cadastre is clipped to the survey first). */
export const SHAPE_LAYER_MAX_FEATURES = 20000;
/** A layer's shapes as stored, at most (the record's limit in Teslyn Field and Teslyn Cloud). */
export const SHAPE_LAYER_MAX_BYTES = 8_000_000;

const round7 = (x: number): number => Math.round(x * 1e7) / 1e7;

/** The field that names a shape, by the usual names; null when none does. */
export function shapeNameField(fields: readonly string[]): string | null {
  const tests: RegExp[] = [
    /^name$/i,
    /^(site|feature|prop(erty)?|lease|holding|station)_?name$/i,
    /name/i,
    /^(label|title)$/i,
    // a survey line's name, "L100270", as Geosoft and QGIS exports of flown lines carry it (MB's radalt flags)
    /^(line|line_?id)$/i,
  ];
  for (const t of tests) {
    const f = fields.find((x) => t.test(x));
    if (f) return f;
  }
  return null;
}

/** A shape's name: its name field's value, else an id or number field's ("LineNumber 100010"), else "<file> n". */
export function shapeFeatureName(
  attributes: ReadonlyArray<readonly [string, string]>,
  layerName: string,
  index: number,
): string {
  const fields = attributes.map(([k]) => k);
  const nf = shapeNameField(fields);
  const nv = nf ? attributes.find(([k]) => k === nf)?.[1].trim() : '';
  if (nv) return nv.slice(0, 200);
  const idf = fields.find((x) => /^id$|_id$|number$|^no$|^num$/i.test(x));
  const iv = idf ? attributes.find(([k]) => k === idf)?.[1].trim() : '';
  if (idf && iv) return `${idf} ${iv}`.slice(0, 200);
  return `${layerName} ${index + 1}`;
}

const closes = (pts: readonly LatLng[]): boolean => {
  if (pts.length < 4) return false;
  const a = pts[0]!;
  const b = pts[pts.length - 1]!;
  return Math.abs(a.lat - b.lat) < 1e-9 && Math.abs(a.lng - b.lng) < 1e-9;
};

/**
 * A shape file reprojected to the map, or why it cannot be added. `file` defaults to the `.shp`'s name. Coordinates
 * that do not fit the `.prj` (metres in a file that says degrees, a northing off the zone) are refused rather than
 * drawn in the wrong place.
 */
export function shapeImport(read: ShapeRead): ShapeImportResult {
  const file = read.files[0] ?? `${read.name}.shp`;
  const crs = shapeCrs(read.prj, file);
  if (!crs.ok) return crs;
  const c = crs.crs;
  let bad: ShapeXy | null = null;
  const to = (p: ShapeXy): LatLng => {
    const [x, y] = p;
    let out: LatLng;
    if (c.kind === 'geographic') {
      if (Math.abs(x) > 180 || Math.abs(y) > 90) bad ??= p;
      out = { lat: y, lng: x };
    } else {
      if (x < 0 || x > 1_000_000 || y < -10_000_000 || y > 10_000_000) bad ??= p;
      out = utmToLatLng(x, y, c.zone, c.hemisphere);
      if (!Number.isFinite(out.lat) || !Number.isFinite(out.lng) || Math.abs(out.lat) > 90) bad ??= p;
    }
    return { lat: round7(out.lat), lng: round7(out.lng) };
  };
  const features: ShapeFeature[] = [];
  let points = 0;
  let lines = 0;
  let areas = 0;
  let empty = 0;
  read.records.forEach((r, i) => {
    const g = r.geometry;
    if (!g) {
      empty += 1;
      return;
    }
    let geometry: KmlGeometry;
    switch (g.type) {
      case 'point':
        geometry = { type: 'point', at: to(g.at) };
        points += 1;
        break;
      case 'multi_point': {
        const parts: KmlGeometry[] = g.points.map((p) => ({ type: 'point', at: to(p) }));
        geometry = parts.length === 1 ? parts[0]! : { type: 'multi', parts };
        points += 1;
        break;
      }
      case 'polyline': {
        const parts: KmlGeometry[] = g.parts.map((p) => ({ type: 'line_string', points: p.map(to) }));
        geometry = parts.length === 1 ? parts[0]! : { type: 'multi', parts };
        if (g.parts.every((p) => closes(p.map(to)))) areas += 1;
        else lines += 1;
        break;
      }
      case 'polygon': {
        const parts: KmlGeometry[] = g.polygons.map((p) => ({
          type: 'polygon',
          outer: p.outer.map(to),
          holes: p.holes.map((h) => h.map(to)),
        }));
        geometry = parts.length === 1 ? parts[0]! : { type: 'multi', parts };
        areas += 1;
        break;
      }
    }
    features.push({ name: shapeFeatureName(r.attributes, read.name, i), geometry, attributes: r.attributes });
  });
  // assigned inside `to`, so TypeScript cannot see it change
  const badAt = bad as ShapeXy | null;
  if (badAt) {
    const [x, y] = badAt;
    return {
      ok: false,
      reason: `The shapes of ${file} do not fit its .prj (${c.words}): a point is at ${x}, ${y}${c.kind === 'geographic' ? ', which is not in degrees' : ''}. Check the .prj is the one that came with the shape file.`,
    };
  }
  if (features.length === 0)
    return {
      ok: false,
      reason: `${file} holds no shapes to draw.${read.notes.length ? ` ${read.notes.join(' ')}` : ''}`,
    };
  if (features.length > SHAPE_LAYER_MAX_FEATURES)
    return {
      ok: false,
      reason: `${file} holds ${features.length.toLocaleString('en-AU')} shapes; Teslyn keeps at most ${SHAPE_LAYER_MAX_FEATURES.toLocaleString('en-AU')} in one layer. Clip it to the survey area first.`,
    };
  const notes = [...read.notes];
  if (empty > 0) notes.push(`${empty} empty ${empty === 1 ? 'shape' : 'shapes'} left out.`);
  const source = read.source.includes('!')
    ? `${read.source
        .split(/[\\/!]/)
        .filter(Boolean)
        .slice(-2)
        .join(' › ')}`
    : file;
  return {
    ok: true,
    layer: {
      name: read.name,
      source,
      files: read.files,
      paths: read.paths,
      crs: c,
      features,
      counts: { points, lines, areas },
      fields: read.fields,
      bounds: boundsOf(features.flatMap((f) => geometryLatLngs(f.geometry))),
      notes,
    },
  };
}

function geometryLatLngs(g: KmlGeometry): LatLng[] {
  switch (g.type) {
    case 'point':
      return [g.at];
    case 'line_string':
      return [...g.points];
    case 'polygon':
      return [...g.outer];
    case 'multi':
      return g.parts.flatMap(geometryLatLngs);
  }
}

/** The shape file's contents in words: "1 area · WGS 84 / UTM zone 50S · fields X1, Y1, X2, Y2". */
export function shapeImportSummary(l: ShapeImport): string {
  const n = (k: number, one: string, many: string): string =>
    `${k.toLocaleString('en-AU')} ${k === 1 ? one : many}`;
  const parts = [
    l.counts.points ? n(l.counts.points, 'point', 'points') : '',
    l.counts.lines ? n(l.counts.lines, 'line', 'lines') : '',
    l.counts.areas ? n(l.counts.areas, 'area', 'areas') : '',
  ].filter(Boolean);
  return [
    parts.join(', '),
    l.crs.words,
    l.fields.length
      ? `fields ${l.fields.slice(0, 8).join(', ')}${l.fields.length > 8 ? ', …' : ''}`
      : 'no fields',
  ].join(' · ');
}

/**
 * Words when the shapes are far from the survey (more than 300 km from its box): a `.prj` naming the wrong zone puts
 * them a zone away, as F26048's Geosoft files stamped zone 59S for a zone 50S survey did. Null when near, or when the
 * survey has no box yet.
 */
export function shapeFarFromSurvey(
  layer: Pick<ShapeImport, 'bounds' | 'crs'>,
  survey: GeoBounds | null,
): string | null {
  const b = layer.bounds;
  if (!b || !survey) return null;
  const gapLat = Math.max(0, survey.south - b.north, b.south - survey.north);
  const midLat = ((survey.north + survey.south) / 2) * (Math.PI / 180);
  const gapLng = Math.max(0, survey.west - b.east, b.west - survey.east) * Math.cos(midLat);
  const km = Math.hypot(gapLat, gapLng) * 111.32;
  if (km <= 300) return null;
  const about = String(Math.round(km / 10) * 10).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `These shapes are about ${about} km from the survey. Check the .prj names the right coordinate system (it says ${layer.crs.words}).`;
}

// ------------------------------------------------------------------------------------------- what kind of layer

export type ShapeLayerKind = 'hazard' | 'property' | 'other';
/** The kinds kept as a shape layer record (property boundaries become landholder boundaries). */
export type ShapeLayerRecordKind = Exclude<ShapeLayerKind, 'property'>;

export interface ShapeKindChoice {
  readonly kind: ShapeLayerKind;
  readonly word: string;
  readonly note: string;
}

/** The question Teslyn asks (decision 921), in the dialog's order. */
export const SHAPE_KIND_CHOICES: readonly ShapeKindChoice[] = [
  {
    kind: 'hazard',
    word: 'Hazards',
    note: 'Drawn with the hazards on every laptop of the project, Teslyn Cloud’s staff maps and Teslyn Mobile, and offered for the pilots’ hazard pack. Clients see them only when you publish the pack to the client portal.',
  },
  {
    kind: 'property',
    word: 'Property boundaries',
    note: 'Each area becomes a landholder boundary on the Landholders tab, in a folder named after the file, shared like the others: notes, flags and folders on every laptop and Teslyn Cloud.',
  },
  {
    kind: 'other',
    word: 'Other layer',
    note: 'Drawn on every laptop of the project, Teslyn Cloud’s staff maps and Teslyn Mobile. Never in the pilots’ hazard pack or on the client portal.',
  },
];

/** Why the shape file cannot be added as `kind`, or null. */
export function shapeKindProblem(kind: ShapeLayerKind | null, layer: ShapeImport): string | null {
  if (!kind) return 'Choose what the shape file is: hazards, property boundaries or another layer.';
  if (kind === 'property' && layer.counts.areas === 0)
    return `None of the shapes in ${layer.source} is an area, so none can be a property boundary. Choose Hazards or Other layer.`;
  return null;
}

/** What is said about property boundaries when the file also holds points or lines. */
export function shapePropertyNote(layer: ShapeImport): string | null {
  const left = layer.counts.points + layer.counts.lines;
  return left > 0
    ? `${left.toLocaleString('en-AU')} ${left === 1 ? 'shape is' : 'shapes are'} not an area and will be left out.`
    : null;
}

/**
 * The areas as a KML document for the landholder import (`landParcelsFromKml`): one folder named after the layer, a
 * line that closes on itself read as the area it bounds (the flight-plan application writes a block boundary as a
 * closed line).
 */
export function shapeAreasDocument(layer: ShapeImport, folder: string = layer.name): KmlDocument {
  const asArea = (g: KmlGeometry): KmlGeometry | null => {
    if (g.type === 'polygon') return g;
    if (g.type === 'line_string')
      return closes(g.points) ? { type: 'polygon', outer: g.points, holes: [] } : null;
    if (g.type === 'multi') {
      const parts = g.parts.map(asArea).filter((x): x is KmlGeometry => x !== null);
      return parts.length === 0 ? null : parts.length === 1 ? parts[0]! : { type: 'multi', parts };
    }
    return null;
  };
  const placemarks: KmlPlacemark[] = layer.features.map((f) => ({
    name: f.name,
    description: '',
    style_url: null,
    style: null,
    visible: true,
    geometry: asArea(f.geometry),
    extended_data: f.attributes,
  }));
  const sub: KmlFolder = {
    name: folder.trim() || layer.name,
    description: '',
    visible: true,
    folders: [],
    placemarks,
  };
  return {
    root: { name: '', description: '', visible: true, folders: [sub], placemarks: [] },
    styles: {},
    extended_data: [],
  };
}

/** The landholder boundaries of a shape file chosen as property boundaries (decisions 618, 921). */
export function shapeLandParcels(
  layer: ShapeImport,
  existing: readonly LandParcel[],
  folder: string = layer.name,
): { readonly parcels: LandParcel[]; readonly skipped: number; readonly duplicates: number } {
  return landParcelsFromKml(shapeAreasDocument(layer, folder), layer.source, existing);
}

// ------------------------------------------------------------------------------------------- the shared record

/** A shape layer of the project (`shape_layer`; Field 0072, Postgres 20261102180000): hazards or another layer. */
export interface ShapeLayerRecord {
  readonly id: string;
  readonly project_id: string;
  readonly name: string;
  readonly kind: ShapeLayerRecordKind;
  readonly source: string;
  /** The coordinate system it came in, in words ("WGS 84 / UTM zone 50S"). */
  readonly crs: string;
  readonly features: readonly ShapeFeature[];
  readonly created_at?: string;
  readonly updated_at?: string;
  readonly deleted_at?: string | null;
}

/** A shape layer to add (`shape_layers_change { save }`). */
export interface ShapeLayerSave {
  readonly id?: string;
  readonly name: string;
  readonly kind: ShapeLayerRecordKind;
  readonly source: string;
  readonly crs: string;
  readonly features: readonly ShapeFeature[];
}

/** The record to save for a shape file chosen as hazards or another layer, or why it cannot be kept. */
export function shapeLayerSave(
  layer: ShapeImport,
  kind: ShapeLayerRecordKind,
  name: string = layer.name,
): { readonly ok: true; readonly save: ShapeLayerSave } | { readonly ok: false; readonly reason: string } {
  const n = name.trim();
  if (!n) return { ok: false, reason: 'Type a name for the layer.' };
  if (n.length > 200) return { ok: false, reason: 'Keep the layer’s name to 200 characters.' };
  const save: ShapeLayerSave = {
    name: n,
    kind,
    source: layer.source.slice(0, 500),
    crs: layer.crs.words,
    features: layer.features,
  };
  const bytes = JSON.stringify(save.features).length;
  if (bytes > SHAPE_LAYER_MAX_BYTES)
    return {
      ok: false,
      reason: `${layer.source} is ${(bytes / 1e6).toFixed(1)} MB as a layer; Teslyn keeps layers of up to ${SHAPE_LAYER_MAX_BYTES / 1e6} MB so every laptop and phone can draw it. Clip it to the survey area or simplify it first.`,
    };
  return { ok: true, save };
}

/** Records as read (Teslyn Cloud gives `features` as JSON; a damaged row has none). */
export function shapeLayersOfRecords(rows: ReadonlyArray<Record<string, unknown>>): ShapeLayerRecord[] {
  const out: ShapeLayerRecord[] = [];
  for (const r of rows) {
    if (r.deleted_at) continue;
    let features = r.features;
    if (typeof features === 'string') {
      try {
        features = JSON.parse(features);
      } catch {
        features = [];
      }
    }
    if (r.kind !== 'hazard' && r.kind !== 'other') continue;
    out.push({
      id: String(r.id ?? ''),
      project_id: String(r.project_id ?? ''),
      name: String(r.name ?? ''),
      kind: r.kind,
      source: String(r.source ?? ''),
      crs: String(r.crs ?? ''),
      features: Array.isArray(features) ? (features as ShapeFeature[]) : [],
      ...(typeof r.created_at === 'string' ? { created_at: r.created_at } : {}),
      ...(typeof r.updated_at === 'string' ? { updated_at: r.updated_at } : {}),
    });
  }
  return out.sort((a, b) => a.name.localeCompare(b.name, 'en-AU'));
}

/** The kind of a layer in words, for the lists and identify. */
export const SHAPE_LAYER_KIND_WORD: Readonly<Record<ShapeLayerRecordKind, string>> = {
  hazard: 'Hazards (shape file)',
  other: 'Shape file layer',
};

/** The key a layer is hidden by in this laptop's map preferences (`hidden_hazard_folders`, never the record). */
export const shapeLayerHideKey = (id: string): string => `shape:${id}`;

/**
 * The shape layers on the map: hazards on the hazards layer in the hazard colour (lines dash-dot over the dark edge,
 * decision 654), other layers on the overlays layer in the accent colour. Every shape names its layer; the identify
 * rows carry the `.dbf` values as text.
 */
export function shapeLayerFeatures(
  layers: readonly ShapeLayerRecord[],
  hidden: ReadonlySet<string> = new Set(),
): LayerResult {
  const out: MapFeature[] = [];
  for (const l of layers) {
    if (hidden.has(shapeLayerHideKey(l.id))) continue;
    const hazard = l.kind === 'hazard';
    l.features.forEach((f, i) => {
      kmlGeometryToMap(f.geometry).forEach((geometry, j) => {
        const base: FeatureStyle = {
          colour: hazard ? 'fail' : 'accent',
          stroke: 'normal',
          dashed: false,
          fill: geometry.type === 'polygon' ? 0.1 : geometry.type === 'point' ? 0.9 : 0,
        };
        out.push({
          id: `shape:${l.id}:${i}.${j}`,
          layer: hazard ? 'hazards' : 'kml',
          geometry,
          style: geometry.type === 'line' && hazard ? { ...base, pattern: 'dash-dot', casing: true } : base,
          tooltip: `${f.name} · ${l.name}`,
          identify: [
            { label: 'Name', value: f.name },
            { label: 'Layer', value: l.name },
            { label: 'Kind', value: SHAPE_LAYER_KIND_WORD[l.kind] },
            { label: 'Source', value: l.source },
            ...f.attributes.filter(([, v]) => v !== '').map(([k, v]) => ({ label: k, value: v })),
          ],
        });
      });
    });
  }
  return { features: out, notDrawn: [] };
}

// --------------------------------------------------------------------------------------------- the dropped files

const SHAPE_PART = /\.(shp|dbf|prj|shx|cpg)$/i;

/** Whether a file is part of a shape file (`.shp`, `.dbf`, `.prj`, `.shx`, `.cpg`). */
export const isShapePart = (path: string): boolean => SHAPE_PART.test(path);

/**
 * The shape files among dropped paths, one path per shape file (its `.shp` if dropped, else the first part): the
 * core reads the others from beside it. A `.zip` is passed as it is (the core reads every shape file in it).
 */
export function shapeDropPaths(paths: readonly string[]): string[] {
  const seen = new Map<string, string>();
  for (const p of paths) {
    if (/\.zip$/i.test(p)) {
      seen.set(p.toLowerCase(), p);
      continue;
    }
    if (!isShapePart(p)) continue;
    const key = p.replace(SHAPE_PART, '').toLowerCase();
    const was = seen.get(key);
    if (!was || (/\.shp$/i.test(p) && !/\.shp$/i.test(was))) seen.set(key, p);
  }
  return [...seen.values()];
}

// --------------------------------------------------------------------------------------------- Teslyn Mobile

/** One shape the phone's map draws: `[lat, lng]` points (a polygon's rings), a token colour and its name. */
export interface PhoneShape {
  readonly kind: 'point' | 'line' | 'polygon';
  readonly rings: ReadonlyArray<ReadonlyArray<readonly [number, number]>>;
  /** A colour token: hazards `fail`, other layers `accent`, landholder boundaries `surface-000` (white on imagery). */
  readonly colour: 'fail' | 'accent' | 'surface-000';
  readonly fill: number;
  readonly label: string;
}

/** At most this many points cross to the phone's web map, over every shape (a phone draws 30,000 smoothly). */
export const PHONE_SHAPE_MAX_POINTS = 30_000;

/**
 * What the phone's Map tab draws of the project's shape file layers and landholder boundaries (decision 921: the
 * phone gets them). Shapes over the budget are thinned evenly, each keeping its ends, so a large layer stays a
 * likeness of itself rather than being cut off; nothing but the shape and its name crosses to the phone's map.
 */
export function phoneShapeScene(
  layers: readonly ShapeLayerRecord[],
  parcels: ReadonlyArray<{ readonly name: string; readonly geometry: KmlGeometry }>,
): PhoneShape[] {
  const out: PhoneShape[] = [];
  const push = (g: KmlGeometry, colour: PhoneShape['colour'], label: string, fill: number): void => {
    for (const m of kmlGeometryToMap(g)) {
      if (m.type === 'point')
        out.push({ kind: 'point', rings: [[[m.at.lat, m.at.lng]]], colour, fill, label });
      else if (m.type === 'line')
        out.push({
          kind: 'line',
          rings: [m.points.map((p) => [p.lat, p.lng] as const)],
          colour,
          fill: 0,
          label,
        });
      else
        out.push({
          kind: 'polygon',
          rings: m.rings.map((r) => r.map((p) => [p.lat, p.lng] as const)),
          colour,
          fill,
          label,
        });
    }
  };
  for (const p of parcels) push(p.geometry, 'surface-000', p.name, 0.05);
  for (const l of layers)
    for (const f of l.features)
      push(f.geometry, l.kind === 'hazard' ? 'fail' : 'accent', `${f.name} · ${l.name}`, 0.1);
  const total = out.reduce((n, s) => n + s.rings.reduce((k, r) => k + r.length, 0), 0);
  if (total <= PHONE_SHAPE_MAX_POINTS) return out;
  const keep = PHONE_SHAPE_MAX_POINTS / total;
  return out.map((s) => ({
    ...s,
    rings: s.rings.map((r) => {
      const want = Math.max(s.kind === 'polygon' ? 4 : 2, Math.round(r.length * keep));
      if (r.length <= want) return r;
      const step = (r.length - 1) / (want - 1);
      return Array.from({ length: want }, (_, i) => r[Math.round(i * step)]!);
    }),
  }));
}
